/**
 * Kampf-Aktionen als reine Funktionen: Server und Offline-Client (Phase 4)
 * wenden damit dieselben Regeln an und schreiben dieselben Log-Zeilen.
 */
import type { z } from 'zod';
import type { updateCombatantSchema } from './api.js';
import {
  advanceTurn,
  applyDeathSaves,
  applyHpChange,
  conditionName,
  DEAD,
  isDead,
  type CombatantState,
  type CombatCondition,
  type CombatTurnState,
  type HpChangeKind,
} from './combat.js';

export const COMBAT_LOG_LIMIT = 300;

/** Regelverstoß (z. B. Änderung an Verstorbenen); der Server antwortet mit 400. */
export class CombatRuleError extends Error {
  constructor(
    message: string,
    public readonly code = 'bad_request',
  ) {
    super(message);
  }
}

export interface CombatLogLine {
  at: string;
  round: number;
  text: string;
}

export function appendCombatLog(log: readonly CombatLogLine[], round: number, texts: readonly string[], at = new Date().toISOString()): CombatLogLine[] {
  return [...log, ...texts.map((text) => ({ at, round, text }))].slice(-COMBAT_LOG_LIMIT);
}

export type CombatantUpdateInput = z.output<typeof updateCombatantSchema>;
export type CombatantFieldPatch = Partial<Omit<CombatantState, 'id' | 'kind' | 'noteId' | 'groupId'>>;

/** Teilnehmer ändern: Felder, Todesrettungswürfe, Zustände – mit Log-Zeilen. */
export function planCombatantUpdate(r: CombatantState, input: CombatantUpdateInput): { patch: CombatantFieldPatch; log: string[] } {
  const { applyToGroup: _group, deathSaves, ...rest } = input;
  const patch: CombatantFieldPatch = { ...rest };
  const log: string[] = [];
  // Verstorbene sind eingefroren – erlaubt bleiben nur Rücknahme und Ordnungsfelder
  const undo = patch.conditions !== undefined && !patch.conditions.some((x) => x.key === DEAD);
  if (isDead(r) && !undo) {
    const allowed = new Set(['name', 'notes', 'sortOrder', 'hidden', 'initiative']);
    if (deathSaves || Object.keys(patch).some((k) => !allowed.has(k))) throw new CombatRuleError(`${r.name} ist verstorben`, 'dead');
  }
  if (deathSaves) {
    // Drei Fehlschläge → verstorben
    const ds = applyDeathSaves({ ...r, ...patch }, deathSaves);
    Object.assign(patch, ds.patch);
    log.push(...ds.events);
  }
  // „Verstorben“ protokolliert der Zustand selbst
  if (patch.defeated === true && !r.defeated && !patch.conditions?.some((x) => x.key === DEAD)) log.push(`${r.name} ist besiegt`);
  if (patch.conditions) {
    const before = new Set(r.conditions.map((x) => x.key));
    for (const x of patch.conditions) if (!before.has(x.key)) log.push(`${r.name}: ${conditionName(x.key)}${x.rounds ? ` (${x.rounds} Runden)` : ''}`);
  }
  return { patch, log };
}

/** Schaden, Heilung, temporäre TP oder TP setzen – mit Hinweisen (z. B. Konzentration). */
export function planHpChange(
  r: CombatantState,
  kind: HpChangeKind,
  amount: number,
  ruleset: '2014' | '2024',
): { patch: CombatantFieldPatch; notices: string[]; concentrationDc: number | null } {
  if (isDead(r)) throw new CombatRuleError(`${r.name} ist verstorben`, 'dead');
  const res = applyHpChange(r, kind, amount, ruleset);
  const notices = [...res.events];
  if (res.concentrationDc) notices.push(`Konzentrationswurf für ${r.name}: SG ${res.concentrationDc}`);
  return { patch: res.patch, notices, concentrationDc: res.concentrationDc };
}

/** Zugwechsel inkl. Kampfbeginn; liefert Log-Zeilen und Hinweise (ausgelaufene Zustände). */
export function planTurn(
  list: readonly CombatantState[],
  state: CombatTurnState,
  direction: 1 | -1,
): { state: CombatTurnState; conditionUpdates: Map<string, CombatCondition[]>; log: string[]; notices: string[] } {
  if (state.status === 'ended') throw new CombatRuleError('Der Kampf ist beendet');
  if (list.length === 0) throw new CombatRuleError('Keine Teilnehmer');
  const res = advanceTurn(list, state, direction);
  const log = res.events.filter((e) => e.type !== 'turn').map((e) => e.text);
  if (state.status === 'prep') log.unshift('Kampf beginnt');
  return {
    state: { ...res.state, status: 'active' },
    conditionUpdates: res.conditionUpdates,
    log,
    notices: res.events.filter((e) => e.type === 'condition-expired').map((e) => e.text),
  };
}
